import { api, ApiRes, BankAccount } from './client'
import {
    KycDocumentType,
    KycDocumentVerificationStatus,
    KycDocumentVerificationType,
    VerificationStatus,
} from '../../typings'
import axios, { AxiosRequestConfig } from 'axios'
import { fileToBase64 } from '../helpers'
import { FrontendBankAccountResource } from './bank-accounts'

interface ExternalKycProcessResponse {
    status: VerificationStatus
    redirect_url: string
    type: string
}

interface SignteqSignatureUrlResponse {
    redirect_url: string
}

interface ExternalKycProcessChange {
    key: string
    old: string | boolean
    new: string | boolean
}

export interface MinimalKycDocument {
    type: KycDocumentType | null
    verification_type: KycDocumentVerificationType
    storage_key_pdf: string | null
    storage_key: string | null
    storage_key_back: string | null
    bankAccount?: BankAccount
    wallet_id?: string
}
export interface KycDocument extends MinimalKycDocument {
    account_id?: string
    url?: string
    mime_type?: string
    valid_to?: string
    verification_status?: string
    verification_comment?: string
    verification_date?: string
    is_external?: string
    is_manual?: string
    created_at?: string
}
export interface KycDocumentSaveRequestBody extends KycDocument {
    natural_profile_uid?: string // uuid
    legal_profile_uid?: string // uuid
}

export interface KycDocumentResponse extends KycDocument {
    id
    account_id
    verification_type
    type
    storage_key
    url
    mime_type
    valid_to
    verification_status: KycDocumentVerificationStatus
    verification_comment
    verification_date
    is_external
    is_manual
    created_at
    bank_account: FrontendBankAccountResource
}

export interface UploadFileResponse {
    key: string
    upload_url: string
    temp_storage_url: string
}

export type ExternalKycProcessChangesResponse = Array<ExternalKycProcessChange>

export const postExternalKycProcess = (restart = false): ApiRes<ExternalKycProcessResponse> =>
    api.post('external-kyc-processes', restart ? { restart: true } : false)

export const setExternalKycProcessStatus = (id: string, status: string): ApiRes<ExternalKycProcessResponse> =>
    api.put(`external-kyc-processes/${id}`, { status: status })

export const getExternalKycProcess = (): ApiRes<ExternalKycProcessResponse> => api.get('external-kyc-processes')

export const getSignteqSignatureUrl = (id: string): ApiRes<SignteqSignatureUrlResponse> =>
    api.get(`external-kyc-processes/signteq-signature-url/${id}`)

export const resetExternalKycProcess = (accountId: string): ApiRes<SignteqSignatureUrlResponse> =>
    api.get(`external-kyc-processes/${accountId}/reset`)

export const storeSignteqSignaturedDocument = (
    documentUrl: string,
    order_uid: string
): ApiRes<SignteqSignatureUrlResponse> =>
    api.post('external-kyc-processes/signteq-signature-url', { documentUrl: documentUrl, orderUid: order_uid })

export const getExternalKycProcessChanges = (): ApiRes<ExternalKycProcessChangesResponse> =>
    api.get('external-kyc-processes/changes')

export const postExternalKycProcessOverwriteNaturalProfile = (): ApiRes<void> =>
    api.post('external-kyc-processes/overwrite-natural-profile')

export const postUploadKycDocument = async (file, config: AxiosRequestConfig = null) => {
    const encodedFile = await fileToBase64(file)
    return api.post('kyc_documents/upload', { image: encodedFile }, config)
}

export const postUploadKycDocumentS3 = (file: File, config: AxiosRequestConfig = null): ApiRes<UploadFileResponse> => {
    return api.post('cdn/kyc_upload_url', { filename: file.name }).then(async (response) => {
        await axios.put(response.data.upload_url, await file.arrayBuffer(), {
            headers: {
                'Content-Type': file.type,
            },
            ...config,
        })
        return response
    })
}

export const postUploadComplianceProfileDocument = async (file, fileName, config: AxiosRequestConfig = null) => {
    const encodedFile = await fileToBase64(file)
    return api.post('compliance_profiles/upload', { image: encodedFile, file_name: fileName }, config)
}

export const postKycDocument = (data: KycDocumentSaveRequestBody): ApiRes<KycDocumentResponse> =>
    api.post('kyc_documents', data)

export const getKycDocumentsForNaturalProfile = (naturalProfileUid): ApiRes<KycDocumentResponse[]> =>
    api.get(`natural-profiles/${naturalProfileUid}/kyc-documents`)

export const getKycDocumentsForLegalProfile = (legalProfileUid): ApiRes<KycDocumentResponse[]> =>
    api.get(`legal-profiles/${legalProfileUid}/kyc-documents`)

export enum KycProvider {
    IDNOW = 'idnow',
    SIGNTEQ = 'signteq',
}
